// SuperCompaniesListPage — /super/companies.
//
// Cross-tenant table of every company. Search + status + tier filters.
// Per-row actions: View (→ /super/companies/:enc_id) and Impersonate
// (confirmation modal → useImpersonateCompany → start session as the
// target company's admin).

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, ShieldCheck, UserCog } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { Input } from '../../components/form/Input';
import { Select } from '../../components/form/Select';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import {
  useAllCompanies,
  useImpersonateCompany,
  type SuperCompanyListItem,
} from './hooks/useSuper';
import styles from './SuperCompaniesListPage.module.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

function statusBadgeClass(s: SuperCompanyListItem['status']): string {
  if (s === 'active') return styles.badgeGreen ?? '';
  if (s === 'suspended') return styles.badgeRed ?? '';
  return styles.badgeMuted ?? '';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB');
}

export function SuperCompaniesListPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const status = params.get('status') ?? '';
  const tier = params.get('tier') ?? '';
  const search = params.get('search') ?? '';

  const filters = useMemo(
    () => ({
      page,
      limit: 25,
      ...(status && { status: status as 'active' | 'inactive' | 'suspended' }),
      ...(tier && { tier }),
      ...(search && { search }),
    }),
    [page, status, tier, search],
  );

  const list = useAllCompanies(filters);
  const impersonate = useImpersonateCompany();

  const [confirmTarget, setConfirmTarget] = useState<SuperCompanyListItem | null>(
    null,
  );

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const companies = list.data?.companies ?? [];
  const total = list.data?.pagination?.total ?? 0;
  const totalPages = list.data?.pagination?.totalPages ?? 1;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Companies"
        subtitle={`${total} tenants — search, filter, and impersonate`}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="ghost"
              icon={ArrowLeft}
              onClick={() => navigate('/super')}
            >
              Back
            </Button>
            <Button variant="primary" onClick={() => navigate('/super/companies/new')}>
              New Company
            </Button>
          </div>
        }
      />

      <Card>
        <div className={styles.filters}>
          <Input
            label="Search"
            value={search}
            onChange={(e) => setParam('search', e.target.value)}
            placeholder="Name or subdomain"
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
            options={STATUS_OPTIONS}
          />
          <Input
            label="Tier"
            value={tier}
            onChange={(e) => setParam('tier', e.target.value)}
            placeholder="e.g. pro, enterprise"
          />
        </div>
      </Card>

      <Card flush>
        {list.isLoading ? (
          <div className={styles.empty}>
            <Spinner />
          </div>
        ) : companies.length === 0 ? (
          <div className={styles.empty}>No companies match the filters.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Subdomain</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th className={styles.numericCol}>Staff</th>
                  <th className={styles.numericCol}>Bookings (30d)</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.enc_id} className={c.deleted_at ? styles.rowDeleted : ''}>
                    <td>
                      <button
                        type="button"
                        className={styles.nameLink}
                        onClick={() =>
                          navigate(`/super/companies/${encodeURIComponent(c.enc_id)}`)
                        }
                      >
                        {c.name}
                      </button>
                    </td>
                    <td className={styles.mono}>{c.subdomain ?? '—'}</td>
                    <td>{c.tier_name ?? c.tier ?? '—'}</td>
                    <td>
                      <span className={`${styles.badge} ${statusBadgeClass(c.status)}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className={styles.numericCol}>{c.staff_count}</td>
                    <td className={styles.numericCol}>{c.booking_count_30d}</td>
                    <td>{formatDate(c.created_at)}</td>
                    <td>
                      <div className={styles.rowActions}>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Eye}
                          onClick={() =>
                            navigate(
                              `/super/companies/${encodeURIComponent(c.enc_id)}`,
                            )
                          }
                        >
                          View
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={UserCog}
                          onClick={() => setConfirmTarget(c)}
                          disabled={c.status !== 'active'}
                        >
                          Impersonate
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {totalPages > 1 ? (
        <div className={styles.pagination}>
          <Button
            variant="ghost"
            size="sm"
            disabled={page === 1}
            onClick={() => setParam('page', String(page - 1))}
          >
            Previous
          </Button>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setParam('page', String(page + 1))}
          >
            Next
          </Button>
        </div>
      ) : null}

      <ConfirmModal
        open={confirmTarget !== null}
        title="Impersonate this company?"
        confirmLabel="Yes, impersonate"
        variant="default"
        loading={impersonate.isPending}
        onConfirm={async () => {
          if (!confirmTarget) return;
          await impersonate.mutateAsync(confirmTarget.enc_id);
          setConfirmTarget(null);
        }}
        onCancel={() => setConfirmTarget(null)}
        message={
          <span className={styles.modalMessage}>
            <ShieldCheck size={16} aria-hidden />
            <span>
              You are about to impersonate{' '}
              <strong>{confirmTarget?.name ?? ''}</strong>. The session lasts
              15 minutes and every action will be audit-logged. Continue?
            </span>
          </span>
        }
      />
    </div>
  );
}
